/* eslint-disable max-lines */
import AppRouter from 'common/AppRouter';
import { useDispatch, useSelector } from 'react-redux';
import {
  onTeamSelection,
  removeStoredTeamIfNotExistsAndSelectTeamIfNeeded
} from 'common/services/teams.service';
import * as TrackingService from 'common/services/tracking.service';
import Background from 'components/Background';
import { Flex, Padding } from 'components/base/layout-components';
import BottomBar from 'components/BottomBar';
import ProcessingBar from 'components/ProcessingBar';
import {
  getProcessingBarProps,
  getProcessingState,
  ProcessingBarStates
} from 'components/ProcessingBar/ProcessingBarHelper';
import Sidebar from 'components/Sidebar/Sidebar';
import TopBar from 'connected-components/TopBar';
import _ from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import {
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation
} from 'react-router-dom';
import Settings from 'views/root/views/home/views/settings';
import Dashboard from './views/dashboard';
import Loader from 'connected-components/Loader';
import {
  LOCAL_STORAGE_KEYS,
  SEARCH_DATE_FORMAT,
  TRACKING
} from 'common/constants/constants';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import MultipleConnectivityIssuesBar from 'connected-components/MultipleConnectivityIssuesBar';
import {
  CAN_EDIT_ACCOUNT_TYPES,
  FEATURE_FLAGS_NAMES,
  ForceUpdateState,
  PROJECT_STATUS
} from '@linearb/linjs-common';
import { isPostOnboardingWithoutReposSelector } from 'state/selectors/flows';

import useCheckTeamPDTStatus from './hooks/useCheckTeamPDTStatus';
import useSettingsService from 'components/hooks/useSettingsService';
import { settingsSelector } from 'state/selectors/settings';
import { DateSelectionChanged } from 'state/callers';
import { shouldShowAllTeamsModeSelector } from 'state/selectors/ui-state';
import useDoraFeatureFlags from './hooks/useDoraFeatureFlags';
import useSidebarState from 'components/Sidebar/hooks/useSidebarState';
import useSidebarButtons from 'components/Sidebar/hooks/useSidebarButtons';
import { ADDITIONAL_TOP_BAR_COMPONENT_MAP } from 'connected-components/TopBar/TopBar.service';
import { syncTeamsTreeSelector } from 'state/selectors/teams';
import NoTeams from 'components/EmptyState/NoTeams';
import ReloginSystem from 'components/EmptyState/ReloginSystem';
import useNoTeams from './hooks/useNoTeams';
import Automations from './views/automations';
import GlobalStyle from '../../../../globalStyles';
import { openSupportRequest } from 'common/services/zendesk.service';
import OnPremStateNotification from 'components/Notification/OnPremStateNotification';
import { lightColorPalette } from '@linearb/linui-common';
import { useSettingsLoading } from './hooks/useSettingsLoading';
import {
  checkIfIterationIsTooLong,
  getCurrentIteration
} from 'common/utils/utils';
import { PERIODS } from 'components/PeriodPicker/periods/constants';
import useScrollToTopOnRouteChange from './hooks/useScrollToTopOnRouteChange';
import InvalidFilterSet from './views/projects/state/filters/components/InvalidFilterSet';
import { CenterLoader } from 'connected-components/Loader/Loader';
import { setRumOrganization } from 'common/services/rum.service';
import { SensorRateLimitWarning } from 'components/SensorRateLimitWarning';
import useUserProfile from './hooks/useUserProfile';
import UserProfileWizardWrapper from 'components/UserProfileWizard/UserProfileWizardWrapper';
import { MigrationStaleBanner } from 'components/MigrationStaleBanner';
import { Surveys } from './views/surveys/Surveys';
import useRateLimitSensorsAuthorizations from 'connected-components/MultipleConnectivityIssuesBar/useRateLimitSensorsAuthorizations';

// eslint-disable-next-line max-lines-per-function
const Home = props => {
  const {
    selectTeamId,
    account = {},
    uiState,
    location,
    getRepositories,
    getContributors,
    getProjects,
    getTeams,
    getBoards,
    getOnboardingJob,
    isTeamLicense,
    isProjectManagementConnected,
    isFinishedOnBoarding,
    iterations,
    configuration,
    contributors,
    reposProcessedPercentage,
    currentTeams,
    organization,
    isSyncContributorsCompleted,
    isProcessingRepos,
    setIsProcessingRepos,
    selectedTeamProject,
    isSlackConnected,
    toggleModal,
    failingSensorsAuthorizations,
    getServices,
    isLoadingIterations,
    lastOnPremisesHeartbeat,
    isDefaultIterationTooLong,
    gitScopeToggle,
    setGitScopeToggle
  } = props;

  const { dates, selectedTeamId, selectedIterationId } = uiState;
  const [disableTeamPicker, setDisableTeamPicker] = useState(false);

  const rateLimitSensorsAuthorizations = useRateLimitSensorsAuthorizations();

  const containerRef = useRef(null);
  const prevDates = useRef();
  useScrollToTopOnRouteChange(containerRef);
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const allSettings = useSelector(settingsSelector);
  const shouldShowAllTeamsMode = useSelector(shouldShowAllTeamsModeSelector);
  const shouldResolveTeamId = isTeamLicense || !shouldShowAllTeamsMode;
  const isPostOnboardingWithoutRepos = useSelector(
    isPostOnboardingWithoutReposSelector
  );
  const treeTeams = useSelector(syncTeamsTreeSelector);
  const { enableTeamSelection, isAllControbutorsTeamSelected } =
    useDoraFeatureFlags();
  const { navigationButtons, buttons } = useSidebarButtons({
    isLoadingIterations,
    isDefaultIterationTooLong,
    disableTeamPicker,
    setDisableTeamPicker,
    gitScopeToggle
  });
  const sidebarButtons = [...navigationButtons, ...buttons];
  const { loadSettings, getSettingOrDefinitionValue } = useSettingsService();

  const { shouldShowWizard, accountId, orgId } = useUserProfile();
  const [openUserProfileWizard, setOpenUserProfileWizard] =
    useState(shouldShowWizard);

  const [isLoading, setIsLoading] = useState(true);
  const { forceSystemUpdateFlow, forceLogoutTimestamp } = useSettingsLoading(
    !isLoading
  );

  const [shouldUpdateProcessingBar, setShouldUpdateProcessingBar] =
    useState(false);

  const { adminToPresent, showNoTeamsEmptyState } = useNoTeams();

  const showForceSystemUpdateEmptyState =
    forceSystemUpdateFlow && forceSystemUpdateFlow !== ForceUpdateState.NONE;

  const isPreventTooLongIterationsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isPreventTooLongIterationsEnabled
  });

  const isSlackOnboardingFFOn = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.enableSlackOnboarding
  });

  const isSlackOnboardingFirstStepFFOn = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.enableSlackOnboardingAsFirstStep
  });

  const isServicesTabEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.servicesTabEnabled
  });

  const isRBACEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isRBACEnabled
  });

  const isUserProfileWizardEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.userProfileWizardEnabled
  });

  const isTeamSpaceEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isTeamSpaceEnabled
  });

  const isShowExpediaMigrationStaleMode = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showExpediaMigrationStaleMode
  });

  const isSurveysEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isSurveysEnabled
  });

  const localStorageAccessor = BrowserStorageAccessor().local;

  const isSlackDissmissed = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.CLOSED_SLACK_BAR
  );

  const { teamPdtStatus } = useCheckTeamPDTStatus(selectedTeamId);
  const loadingPDTProject = teamPdtStatus !== PROJECT_STATUS.STEADY;

  const loadStaticData = async () => {
    getContributors();
    getProjects();

    await Promise.all([
      getTeams({ lean_team: true }),
      getBoards({ is_custom: true })
    ]);

    // silently fetch the boards and the teams with contributors
    getTeams();
    getBoards({ with_references: true });
  };

  const hideProcessingBar = () => setShouldUpdateProcessingBar(true);

  const afterOrgLoad = async account => {
    // Need to call this right after getOrganization so this data is updated before the new visit is logged
    TrackingService.trackOrgProperties(account, organization);
    setRumOrganization(organization);

    if (
      _.get(organization, 'id') &&
      BrowserStorageAccessor(_.get(organization, 'id')).local.get(
        LOCAL_STORAGE_KEYS.IS_PROCESSING_REPOS
      )
    ) {
      getRepositories();
      setIsProcessingRepos(true);
    }

    try {
      getOnboardingJob();
      await loadStaticData();
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!!treeTeams.length) {
      removeStoredTeamIfNotExistsAndSelectTeamIfNeeded(
        currentTeams,
        selectTeamId,
        shouldResolveTeamId,
        treeTeams
      );
    }
  }, [isRBACEnabledFF, treeTeams.length]);

  useEffect(() => {
    (async () => {
      await afterOrgLoad(account);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (isServicesTabEnabled) {
        await getServices();
      }
    })();
  }, [isServicesTabEnabled]);

  useEffect(() => {
    TrackingService.updatePage();
  }, [pathname, search]);

  useEffect(() => {
    if (openUserProfileWizard) {
      TrackingService.logEvent(TRACKING.EVENTS.USER_PROFILE.EVENT, {
        event_type: TRACKING.EVENTS.USER_PROFILE.EVENT_TYPE.WIZARD_PRESENTED,
        user_id: accountId,
        org_id: orgId
      });
    }
  }, [openUserProfileWizard]);

  useEffect(() => {
    if (shouldUpdateProcessingBar) {
      setShouldUpdateProcessingBar(false);
    }
  }, [shouldUpdateProcessingBar]);

  if (window.TrackJS) {
    window.TrackJS.addMetadata('version', process.env.REACT_APP_TAG);
    window.DD_LOGS &&
      window.DD_LOGS.logger.addContext('version', process.env.REACT_APP_TAG);
  }

  useEffect(() => {
    if (organization) {
      TrackingService.trackOrgProperties(account, organization);
      setRumOrganization(organization);
    }
  }, [organization]);

  useEffect(() => {
    if (isFinishedOnBoarding) {
      // todo fix double call
      getContributors();
      // if user did onboarding v2 through RepositoriesModalV2 :
      const userSavedReposTimeStamp = BrowserStorageAccessor().local.get(
        LOCAL_STORAGE_KEYS.USER_SAVED_REPOS_TS
      );
      if (userSavedReposTimeStamp) {
        (async () => {
          const repositories = await getRepositories();

          TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
            page: TRACKING.PAGES.SETTINGS,
            page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
            click_type: TRACKING.CLICK.SAVE,
            num_of_repos: repositories.length
          });
        })();

        BrowserStorageAccessor().local.remove(
          LOCAL_STORAGE_KEYS.USER_SAVED_REPOS_TS
        );
      }
    }
  }, [isFinishedOnBoarding]);

  useEffect(() => {
    if (
      isTeamLicense &&
      !uiState.selectedTeamId &&
      currentTeams &&
      currentTeams.length
    ) {
      onTeamSelection({
        team: currentTeams[0],
        reduxActionFn: selectTeamId,
        history,
        isTeamLicense
      });
    }
  }, [currentTeams, isTeamLicense]);

  const shouldShowProcessingBar = (
    isSyncContributorsCompleted,
    isSlackOnboardingFFOn,
    isSlackConnected
  ) => {
    if (!iterations.length || isPostOnboardingWithoutRepos) {
      return false;
    }

    if (!isFinishedOnBoarding || isProcessingRepos) {
      return true;
    }
    const processingState = getProcessingState({
      isTeamLicense,
      isSyncContributorsCompleted,
      isProjectManagementConnected,
      isFinishedOnBoarding,
      isProcessingRepos,
      isSlackConnected,
      isSlackOnboardingFFOn,
      isSlackDissmissed,
      isSlackOnboardingFirstStepFFOn,
      isAllControbutorsTeamSelected
    });

    switch (processingState) {
      case ProcessingBarStates.CONNECT_PM:
        return !localStorageAccessor.get(LOCAL_STORAGE_KEYS.CLOSED_PM_BAR);
      case ProcessingBarStates.PROCESSING_REPOS:
        return true;
      case ProcessingBarStates.CONNECT_SLACK:
        return !isSlackDissmissed;
      default:
        return false;
    }
  };
  const shouldShowTabsBasedOnLicense =
    !isTeamLicense ||
    (isTeamLicense && (isFinishedOnBoarding || isSyncContributorsCompleted));
  const routesByPath = _.keyBy(AppRouter, 'path');
  const viewPath = location.pathname.split('/')[1];

  const basicDataLoaded =
    dates?.to !== null && dates?.from !== null && !!allSettings.length;

  const showNavigation =
    viewPath !== 'overdue' &&
    shouldShowTabsBasedOnLicense &&
    !isPostOnboardingWithoutRepos &&
    basicDataLoaded;

  const { sidebarWidth, topbarHeight } = useSidebarState(showNavigation);

  const isPulsePage = `/${viewPath}` === AppRouter.PULSE.path;
  const isPulseCta = isPulsePage && isTeamLicense;
  const shouldHidePickers =
    (!enableTeamSelection && currentTeams.length === 0) || isPulseCta;

  const showDatePicker =
    _.get(routesByPath, `[/${viewPath}].options.showDatePicker`, false) &&
    !shouldHidePickers;
  const showDisabledDatePicker = _.get(
    routesByPath,
    `[/${viewPath}].options.showDisabledDatePicker`,
    false
  );

  const showMigrationStaleBanner =
    isShowExpediaMigrationStaleMode &&
    _.get(
      routesByPath,
      `[/${viewPath}].options.showMigrationStaleBanner`,
      false
    );

  // AdditionalTopBarComponent is only exists for PulseIterationPicker on /Pulse view
  const AdditionalTopBarComponent = !isTeamSpaceEnabled
    ? ADDITIONAL_TOP_BAR_COMPONENT_MAP[
        _.get(
          routesByPath,
          `[/${viewPath}].options.AdditionalTopBarComponent`,
          null
        )
      ]
    : null;

  const showTeamPicker =
    (!isPulsePage || !isTeamSpaceEnabled) &&
    _.get(routesByPath, `[/${viewPath}].options.showTeamPicker`, false) &&
    !shouldHidePickers &&
    !!treeTeams.length;

  const show30daysMessage = _.get(
    routesByPath,
    `[/${viewPath}].options.show30daysMessage`,
    false
  );

  const showFilteredView = _.get(
    routesByPath,
    `[/${viewPath}].options.showFilteredView`,
    false
  );

  const iterationsPeriodOnly = _.get(
    routesByPath,
    `[/${viewPath}].options.iterationsPeriodOnly`,
    true
  );

  useEffect(() => {
    if (viewPath !== 'dashboard') {
      return;
    }
    const currentIteration = getCurrentIteration(
      iterations,
      dates.from,
      dates.to,
      selectedIterationId
    );
    const datesChanged = !(
      prevDates?.current?.from === dates.from &&
      prevDates?.current?.to === dates.to
    );
    if (
      datesChanged &&
      currentIteration &&
      checkIfIterationIsTooLong({
        iteration: currentIteration,
        isPreventTooLongIterationsEnabled
      }) &&
      !iterationsPeriodOnly
    ) {
      DateSelectionChanged({
        start_date: PERIODS.PAST_30_DAYS.after.format(SEARCH_DATE_FORMAT),
        end_date: PERIODS.PAST_30_DAYS.before.format(SEARCH_DATE_FORMAT),
        selectedPeriodKey: PERIODS.PAST_30_DAYS.periodKey
      })(dispatch);
      prevDates.current = dates;
    }
  }, [dates, viewPath]);

  const isIterationSelected = dates.to || dates.from;

  const teamProjectId = _.get(
    selectedTeamProject,
    'filter.projects[0].provider_id',
    null
  );

  if (!configuration) {
    return null;
  }

  const SettingsView = <Settings loadSettings={loadSettings} />;

  const _getSidebarRoutes = ({ settingsEnabled = false } = {}) => {
    if (
      showForceSystemUpdateEmptyState &&
      forceSystemUpdateFlow === ForceUpdateState.LOGOUT
    ) {
      return (
        <Route
          path={AppRouter.RELOGIN_SYSTEM.path}
          render={() => (
            <ReloginSystem forceLogoutTimestamp={forceLogoutTimestamp} />
          )}
        />
      );
    }
    if (showNoTeamsEmptyState) {
      return (
        <Route
          path={AppRouter.NO_TEAMS.path}
          render={() => <NoTeams adminToPresent={adminToPresent} />}
        />
      );
    }

    if (!basicDataLoaded) {
      return <></>;
    }

    return iterations.length ? (
      !!isIterationSelected && !!allSettings.length && (
        <>
          <Route
            path={AppRouter.ROOT.path}
            exact
            render={() => (
              <Dashboard
                isLoadingIterations={isLoadingIterations}
                isDefaultIterationTooLong={isDefaultIterationTooLong}
                gitScopeToggle={gitScopeToggle}
              />
            )}
          />

          {settingsEnabled ? (
            <Route path={AppRouter.SETTINGS.path} render={() => SettingsView} />
          ) : (
            <Route path={AppRouter.SETTINGS.path}>
              <Redirect to={AppRouter.PAGE_DASHBOARD.path} />
            </Route>
          )}

          <Route
            exact
            path={AppRouter.INVALID_FILTER_SET.path}
            component={InvalidFilterSet}
          />

          <Route
            path={AppRouter.AUTOMATIONS.path}
            render={() => <Automations isTeamLicense={isTeamLicense} />}
            exact
          />
          <Route
            path={AppRouter.MARKETPLACE.path}
            render={() => (
              <Automations isTeamLicense={isTeamLicense} marketplace />
            )}
            exact
          />
          <Route
            path={`${AppRouter.AUTOMATIONS.path}/not-connected`}
            render={() => <Automations isTeamLicense={isTeamLicense} />}
            exact
          />

          <Route
            path={`${AppRouter.AUTOMATIONS.path}/not-supported`}
            render={() => <Automations isTeamLicense={isTeamLicense} />}
            exact
          />
          <Route
            path={`${AppRouter.MARKETPLACE.path}/add-automation/:id`}
            render={() => <Automations isTeamLicense={isTeamLicense} />}
            exact
          />
          <Route
            path={`${AppRouter.AUTOMATIONS_EXPLORATIONS.path}`} // Return this when make things generic - /:action`}
            render={() => <Automations isTeamLicense={isTeamLicense} />}
            exact
          />

          {isSurveysEnabled ? (
            <Route
              path={`${AppRouter.SURVEYS.path}`}
              render={() => <Surveys />}
              exact
            />
          ) : (
            <Route path={AppRouter.SURVEYS.path}>
              <Redirect to={AppRouter.PAGE_DASHBOARD.path} />
            </Route>
          )}

          {sidebarButtons.map(button => {
            const { subRoutes = [], menu = [] } = button;
            const menuSubRoutes = menu
              .flatMap(({ subRoutes }) => subRoutes)
              .filter(Boolean);
            const buttonRoutes = [
              button,
              ...subRoutes,
              ...menu,
              ...menuSubRoutes
            ];

            return buttonRoutes.map((button, idx) => {
              const { component: Component } = button;

              return Component ? (
                <Route
                  key={`${button.path}${idx}`}
                  exact={button.isExact ?? true}
                  path={button.routePath || button.path}
                  render={props =>
                    React.cloneElement(button.component, { ...props })
                  }
                />
              ) : null;
            });
          })}
        </>
      )
    ) : (
      <Background />
    );
  };

  const LoaderAndBars = () => {
    return (
      <>
        <Loader />
        {shouldShowProcessingBar(
          isSyncContributorsCompleted,
          isSlackOnboardingFFOn,
          isSlackConnected
        ) && (
          <Flex ml='small' mr='small' mt='medium'>
            <ProcessingBar
              processingText={
                isTeamLicense && !contributors.length
                  ? 'Looking for your team members...'
                  : `Processing your repositories... ${
                      reposProcessedPercentage || ''
                    }`
              }
              {...getProcessingBarProps({
                hideProcessingBar,
                isTeamLicense,
                isProcessingRepos,
                isSyncContributorsCompleted,
                isProjectManagementConnected,
                teamProjectId,
                history,
                isFinishedOnBoarding,
                isSlackOnboardingFFOn,
                isSlackConnected,
                selectedTeamId,
                isSlackOnboardingFirstStepFFOn,
                isSlackDissmissed,
                toggleModal,
                isAllControbutorsTeamSelected
              })}
            />
          </Flex>
        )}
        {showMigrationStaleBanner && <MigrationStaleBanner />}
        {lastOnPremisesHeartbeat && (
          <Padding l='small' r='small' t='small'>
            <OnPremStateNotification
              lastOnPremisesHeartbeat={lastOnPremisesHeartbeat}
              openRequest={openSupportRequest}
            />
          </Padding>
        )}
        {(failingSensorsAuthorizations?.length ||
          rateLimitSensorsAuthorizations?.length) &&
        isFinishedOnBoarding ? (
          <MultipleConnectivityIssuesBar
            failingSensorsAuthorizations={failingSensorsAuthorizations}
            rateLimitSensorsAuthorizations={rateLimitSensorsAuthorizations}
          />
        ) : (
          <SensorRateLimitWarning />
        )}
      </>
    );
  };

  const ContentAndBottomBar = () => {
    const canEdit = CAN_EDIT_ACCOUNT_TYPES.includes(account?.type_id);

    return (
      <>
        <Switch>
          {isPostOnboardingWithoutRepos ? (
            <Route path={AppRouter.SETTINGS.path} render={() => SettingsView} />
          ) : (
            _getSidebarRoutes({ settingsEnabled: canEdit })
          )}
        </Switch>
        {!!iterations.length && !!allSettings.length && (
          <BottomBar
            organizationName={_.get(organization, 'name')}
            showBetaLink={_.get(account, 'email', '').includes('linearb.io')}
          />
        )}
      </>
    );
  };

  return (
    <>
      {isLoading && <CenterLoader />}
      {!isLoading &&
      !showNoTeamsEmptyState &&
      !showForceSystemUpdateEmptyState ? (
        <Flex height='100%'>
          <GlobalStyle />
          {showNavigation && <Sidebar />}
          <Flex
            className='home-view-id'
            width={sidebarWidth === 0 ? '100%' : `calc(100% - ${sidebarWidth})`}
            ml={sidebarWidth}
            direction='column'
            position='relative'
          >
            {isUserProfileWizardEnabled && (
              <UserProfileWizardWrapper
                openUserProfileWizard={openUserProfileWizard}
                setOpenUserProfileWizard={setOpenUserProfileWizard}
              />
            )}

            <Route
              render={props => (
                <>
                  {organization && (
                    <TopBar
                      {...props}
                      loadingPDTProject={loadingPDTProject}
                      showDatePicker={showDatePicker}
                      showDisabledDatePicker={showDisabledDatePicker}
                      showTeamPicker={showTeamPicker}
                      show30daysMessage={show30daysMessage}
                      showTabsNavigation={showNavigation}
                      isOnTopOfModals={viewPath === 'overdue'}
                      isClickableLogo={
                        viewPath !== 'overdue' && !isPostOnboardingWithoutRepos
                      }
                      isOnboarding={false}
                      AdditionalTopBarComponent={AdditionalTopBarComponent}
                      shouldHidePickers={shouldHidePickers}
                      height={topbarHeight}
                      isUserProfileWizardEnabled={isUserProfileWizardEnabled}
                      setOpenUserProfileWizard={setOpenUserProfileWizard}
                      disableTeamPicker={disableTeamPicker}
                      setDisableTeamPicker={setDisableTeamPicker}
                      showFilteredView={showFilteredView}
                      viewPath={viewPath}
                      gitScopeToggle={gitScopeToggle}
                      setGitScopeToggle={setGitScopeToggle}
                    />
                  )}
                </>
              )}
            />
            <Flex
              ref={containerRef}
              className='home-view-content'
              direction='column'
              overflow='auto'
              grow='1'
              style={{
                borderTop: `1rem solid ${lightColorPalette.lbBlue_10}`
              }}
            >
              <Flex
                direction='column'
                width='100%'
                height='100%'
                style={{ marginTop: '-1rem' }}
              >
                {LoaderAndBars()}
                {ContentAndBottomBar()}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      ) : (
        !isLoading && !isLoadingIterations && ContentAndBottomBar()
      )}
    </>
  );
};

export default Home;
